import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';
import '../styles/cartPickerPage.css';

const CartPickerPage = () => {
  const { po_number, cart_code } = useParams();
  const { userRole, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [finalizeError, setFinalizeError] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [orderData, setOrderData] = useState(location.state?.orderData);
  const [requestingItemId, setRequestingItemId] = useState(null);
  const [reqQty, setReqQty] = useState('');
  const [reqNote, setReqNote] = useState('');
  const [reqSaving, setReqSaving] = useState(false);
  const [reqError, setReqError] = useState(null);

  useEffect(() => {
    const fetchOrderData = async () => {
      if (!token || orderData) return;

      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/get-po/?po_number=${po_number}`, {
          headers: {
            'Authorization': `Token ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data) {
            setOrderData(data);
          } else {
            setError('No se encontró el pedido especificado.');
          }
        } else {
          setError('Error al cargar el pedido: ' + response.statusText);
        }
      } catch (err) {
        setError('Error de conexión: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [token, po_number, orderData]);

  if (loading) {
    return (
      <div className="loading">
        <p>Cargando información del pedido...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <p>{error}</p>
        <button onClick={() => navigate('/picking')} className="back-button">
          Volver
        </button>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="error">
        <p>No se encontró información del pedido.</p>
        <button onClick={() => navigate('/picking')} className="back-button">
          Volver
        </button>
      </div>
    );
  }

  const items = orderData.items || [];
  const nonFrozenItems = items.filter(item => !item.sku_frozen);
  const frozenItems = items.filter(item => item.sku_frozen);

  const allItemsOk = nonFrozenItems.every(item => item.status === "ok");
  const allFrozenItemsOk = frozenItems.every(item => item.status === "ok");

  const finalizeOrder = async () => {
    if (!token) {
      setError('No token available');
      return;
    }

    if (finalizing) return;
    setFinalizing(true);

    const requestBody = {
      po_number: orderData.po_number,
    };

    if (userRole === 'picker') {
      requestBody.item_status = true;
    } else if (userRole === 'frozen') {
      requestBody.frozen_status = true;
    }

    try {
      const response = await fetch(`${API_BASE}/update-purchase-order/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Order finalized:', data);
        setFinalizeError(null);

        if (userRole === 'picker' || userRole === 'frozen') {
          navigate('/picking');
        }
      } else {
        // Finishing picking no longer contacts Loken — that push now happens
        // when a checker completes verification. A non-OK response here means
        // some line isn't ready (unpicked or awaiting an approved change).
        // Surface a clear, retryable message instead of failing silently or
        // wiping the page.
        let detail = '';
        try {
          const errData = await response.json();
          detail = errData.error || errData.detail || '';
        } catch (parseErr) {
          detail = response.statusText;
        }
        console.error('No se pudo finalizar el pedido:', detail);
        setFinalizeError(
          detail || 'No se pudo finalizar el pedido. Revisa que todos los productos estén pickeados o con su modificación aprobada, y vuelve a intentarlo.'
        );
        setFinalizing(false);
      }
    } catch (error) {
      console.error('Error de conexión al finalizar el pedido:', error);
      setFinalizeError(
        'No se pudo conectar para finalizar el pedido, así que NO se finalizó. Vuelve a tocar "Finalizar Pedido" para reintentar.'
      );
      setFinalizing(false);
    }
  };

  // Re-read the order so the request badges/quantities refresh after a request.
  const refreshOrder = async () => {
    try {
      const response = await fetch(`${API_BASE}/get-po/?po_number=${po_number}`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data) setOrderData(data);
      }
    } catch (err) {
      // Keep the current view if the refresh fails.
    }
  };

  const openRequest = (item) => {
    setRequestingItemId(item.id);
    setReqQty(String(item.ordered_quantity));
    setReqNote('');
    setReqError(null);
  };

  const cancelRequest = () => {
    setRequestingItemId(null);
    setReqError(null);
  };

  const submitRequest = async (item) => {
    setReqSaving(true);
    setReqError(null);
    try {
      const response = await fetch(`${API_BASE}/quantity-requests/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          po_number: po_number,
          item_id: item.id,
          requested_quantity: reqQty,
          note: reqNote,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setReqError(data.error || 'No se pudo enviar la solicitud.');
        return;
      }
      setRequestingItemId(null);
      await refreshOrder();
    } catch (err) {
      setReqError('No se pudo enviar la solicitud. Revisa tu conexión.');
    } finally {
      setReqSaving(false);
    }
  };

  // Always available on an un-picked line (stock may not be real), unless a
  // request is already pending for it. Rendered outside the <Link> so tapping
  // it doesn't start the scan flow.
  const renderRequestControls = (item) => {
    if (item.latest_request && item.latest_request.status === 'pending') return null;
    if (requestingItemId === item.id) {
      return (
        <div className="req-form">
          <label>
            Cantidad real que hay (puede ser 0):
            <input
              type="number"
              min="0"
              max={item.ordered_quantity}
              step={item.format_unit_kg ? '0.001' : '1'}
              value={reqQty}
              onChange={(e) => setReqQty(e.target.value)}
            />
          </label>
          <label>
            Nota (opcional):
            <input
              type="text"
              value={reqNote}
              onChange={(e) => setReqNote(e.target.value)}
              placeholder="Motivo o detalle"
            />
          </label>
          {reqError && <div className="req-error">{reqError}</div>}
          <div className="req-actions">
            <button type="button" onClick={() => submitRequest(item)} disabled={reqSaving}>
              {reqSaving ? 'Enviando...' : 'Enviar solicitud'}
            </button>
            <button type="button" className="req-cancel" onClick={cancelRequest} disabled={reqSaving}>
              Cancelar
            </button>
          </div>
        </div>
      );
    }
    return (
      <button type="button" className="req-button" onClick={() => openRequest(item)}>
        Solicitar modificación de cantidad
      </button>
    );
  };

  return (
    <div className="cart-picker-page2">
      <div className="cartpicker-header">
        <button
          className="cartpicker-back-button"
          onClick={() => navigate('/picking')}
        >
          <span className="cartpicker-arrow">&larr;</span>
        </button>
        <div className="cartpicker-title-container">
          <h1 className="cartpicker-title">
            Productos pedido: {orderData.po_number ? `${orderData.po_number} - ${orderData.client} - ${cart_code}` : 'No se encontró información'}
          </h1>
        </div>
      </div>

      {userRole === 'picker' ? (
        nonFrozenItems.length > 0 ? (
          <div>
            <ul className="item-list">
              {nonFrozenItems.map(item => {
                const itemLink = `/picker/${po_number}/${cart_code}/${item.sku_code}`;
                return (
                  <li key={item.sku_code} className="item">
                    {item.status === "ok" ? (
                      <div className="item-details">
                        <div className="item-group">
                          <span className="sku-name">{item.sku_name}</span>
                          <span className='item'>Cantidad por {item.format_name}:</span>
                          <span className='item'>{item.format_quantity}</span>
                          <span className='item'>SKU {item.sku_code}</span>
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={itemLink}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {item.latest_request && item.latest_request.status === 'pending' && (
                          <span className="item-pending-request">
                            Esperando aprobación ({item.latest_request.requested_quantity})
                          </span>
                        )}
                        {item.latest_request && item.latest_request.status === 'rejected' && (
                          <span className="item-rejected-request">
                            Rechazado: «{item.latest_request.resolution_note}»
                          </span>
                        )}
                        {item.latest_request && item.latest_request.status === 'approved' && (
                          <span className="item-approved-request">
                            Aprobado: {item.latest_request.approved_quantity}
                          </span>
                        )}
                        <div className="item-details">
                          <div className="item-group">
                            <span className="sku-name">{item.sku_name}</span>
                            <span className='item'>Cantidad por {item.format_name}:</span>
                            <span className='item'>{item.format_quantity}</span>
                            <span className='item'>SKU {item.sku_code}</span>
                          </div>
                        </div>
                      </Link>
                    )}
                    <div className="quantity-format-container">
                      <div className='quantity-format-container-2'>
                        <div className="ordered-quantity-box">
                          <span className="ordered-quantity">
                            {item.format_unit_kg && item.status === "ok"
                              ? item.actual_quantity
                              : item.ordered_quantity}
                            {item.format_unit_kg && <span className="unit-label"> kg</span>}
                          </span>
                        </div>
                        <span className="format-name">{item.format_name}</span>
                      </div>
                      <div className={`status-indicator item-status ${item.status}`}></div>
                    </div>
                    {item.status !== "ok" && renderRequestControls(item)}
                  </li>
                );
              })}
            </ul>
            {allItemsOk && (
              <button
                onClick={finalizeOrder}
                className="finalize-button"
                disabled={finalizing}
              >
                {finalizing ? 'Finalizando...' : 'Finalizar Pedido'}
              </button>
            )}
          </div>
        ) : (
          <p>No hay productos no congelados en este pedido.</p>
        )
      ) : (
        frozenItems.length > 0 ? (
          <div>
            <ul className="item-list">
              {frozenItems.map(item => {
                const itemLink = `/picker/${po_number}/${cart_code}/${item.sku_code}`;
                return (
                  <li key={item.sku_code} className="item">
                    {item.status === "ok" ? (
                      <div className="item-details">
                        <span className="sku-name">{item.sku_name}</span>
                        <span className='item'>Cantidad por {item.format_name}:</span>
                        <span className='item'>
                          {item.format_quantity}
                          {item.format_unit_kg && <span className="unit-label"> kg</span>}
                        </span>
                        <span className='item'>SKU {item.sku_code}</span>
                      </div>
                    ) : (
                      <Link
                        to={itemLink}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {item.latest_request && item.latest_request.status === 'pending' && (
                          <span className="item-pending-request">
                            Esperando aprobación ({item.latest_request.requested_quantity})
                          </span>
                        )}
                        {item.latest_request && item.latest_request.status === 'rejected' && (
                          <span className="item-rejected-request">
                            Rechazado: «{item.latest_request.resolution_note}»
                          </span>
                        )}
                        {item.latest_request && item.latest_request.status === 'approved' && (
                          <span className="item-approved-request">
                            Aprobado: {item.latest_request.approved_quantity}
                          </span>
                        )}
                        <div className="item-details">
                          <span className="sku-name">{item.sku_name}</span>
                          <span className='item'>Cantidad por {item.format_name}:</span>
                          <span className='item'>
                            {item.format_quantity}
                            {item.format_unit_kg && <span className="unit-label"> kg</span>}
                          </span>
                          <span className='item'>SKU {item.sku_code}</span>
                        </div>
                      </Link>
                    )}
                    <div className="quantity-format-container">
                      <div className='quantity-format-container-2'>
                        <div className="ordered-quantity-box">
                          <span className="ordered-quantity">
                            {item.format_unit_kg && item.status === "ok"
                              ? item.actual_quantity
                              : item.ordered_quantity}
                            {item.format_unit_kg && <span className="unit-label"> kg</span>}
                          </span>
                        </div>
                        <span className="format-name"> en {item.format_name}</span>
                      </div>
                      <div className={`status-indicator item-status ${item.status}`}></div>
                    </div>
                    {item.status !== "ok" && renderRequestControls(item)}
                  </li>
                );
              })}
            </ul>
            {allFrozenItemsOk && (
              <button
                onClick={finalizeOrder}
                className="finalize-button"
                disabled={finalizing}
              >
                {finalizing ? 'Finalizando...' : 'Finalizar Pedido'}
              </button>
            )}
          </div>
        ) : (
          <p>No hay productos congelados en este pedido.</p>
        )
      )}

      {finalizeError && <div className="error-message finalize-error">{finalizeError}</div>}
    </div>
  );
};

export default CartPickerPage;
